import { ErrorTranslation } from '@surface-elements/shared/util-error';

/**
 * Job-specific error translations based on actual API responses.
 * These override base translations when dealing with job-related endpoints.
 */
export const JOB_ERROR_TRANSLATIONS: ErrorTranslation[] = [
  // Specific API error: "jobId not found!"
  {
    code: 'jobs.not_found',
    message:
      'The job could not be found. It may have been deleted or you may not have permission to view it.',
    title: 'Job Not Found',
    matcher: error =>
      error.status === 404 && error.error?.message === 'jobId not found!',
    priority: 10,
  },

  // Duplicate job name error (within same account)
  {
    code: 'jobs.duplicate_name',
    message:
      'A job with this name already exists for this account. Please choose a different name.',
    title: 'Duplicate Job Name',
    matcher: error =>
      error.error?.message === 'duplicate' && error.url?.includes('/jobs'),
    priority: 10,
  },

  // Generic 404 on jobs endpoint
  {
    code: 'jobs.resource_not_found',
    message: 'The requested job resource could not be found.',
    title: 'Not Found',
    matcher: error => error.status === 404 && error.url?.includes('/jobs'),
    priority: 5,
  },

  // Job contact creation errors
  {
    code: 'jobs.contact_creation_failed',
    message:
      'Unable to add the contact to this job. Please verify the contact information and try again.',
    title: 'Contact Creation Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/jobs/') &&
      error.url?.includes('/contacts'),
    priority: 7,
  },

  // 400 errors on jobs endpoint
  {
    code: 'jobs.bad_request',
    message:
      'The job request could not be processed. Please check your input and try again.',
    title: 'Invalid Job Request',
    matcher: error => error.status === 400 && error.url?.includes('/jobs'),
    priority: 5,
  },

  // Validation errors for jobs
  {
    code: 'jobs.validation_error',
    message:
      'The job information provided is invalid. Please check all required fields are filled correctly.',
    title: 'Validation Error',
    matcher: error => error.status === 422 && error.url?.includes('/jobs'),
    priority: 6,
  },

  // 500 errors on jobs endpoint
  {
    code: 'jobs.server_error',
    message:
      'Unable to process your job request. Our team has been notified. Please try again later.',
    title: 'Job Processing Error',
    matcher: error => error.status === 500 && error.url?.includes('/jobs'),
    priority: 5,
  },

  // Job creation specific
  {
    code: 'jobs.creation_failed',
    message:
      'Unable to create the job. Please verify all required information is provided and the job name is unique for this account.',
    title: 'Job Creation Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/jobs') &&
      error.url?.endsWith('/jobs'), // POST to /jobs
    priority: 6,
  },

  // Job update specific
  {
    code: 'jobs.update_failed',
    message:
      'Unable to update the job. The job may have been modified by another user or have active orders.',
    title: 'Job Update Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/jobs/') &&
      !error.url?.includes('/contacts'), // PATCH to /jobs/:id
    priority: 6,
  },
];

/**
 * Provider configuration for job error translations
 */
export const JOB_ERROR_PROVIDERS = [
  {
    provide: 'ERROR_TRANSLATION_CONFIG',
    useValue: {
      translations: JOB_ERROR_TRANSLATIONS,
      appName: 'jobs-domain',
    },
    multi: true,
  },
];
